import React, { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import api from "@/lib/api";
import Logo from "@/components/Logo";
import WhatsAppFab from "@/components/WhatsAppFab";
import { WHATSAPP_LINK, CLINIC, DOCTOR_DESK, DOCTOR_HEART } from "@/lib/constants";
import {
  Heart, Activity, Stethoscope, Droplets, Wind, Star, MapPin, Clock, Phone,
  Mail, ChevronRight, ShieldCheck, Cpu, HeartPulse, Award, Send, Menu, X, Quote,
} from "lucide-react";

const TESTIMONIALS = [
  { name: "Aquiles Moncayo", text: "Me diagnosticaron hipertensión sin exámenes. Y me hicieron un ecocardiograma que decía que mi corazón estaba muy contraído, vine con el Dr. Baus por una segunda opinión y realizó una evaluación completa e indicó un tratamiento adecuado que mejoró mi calidad de vida." },
  { name: "José Yagual", text: "Me detectaron una catarata y, durante mi pre-quirúrgico en Cenincardio, el Dr. Baus descubrió que no podía operarme por una arritmia. Gracias a este diagnóstico, recibí tratamiento y pude operarme con seguridad." },
  { name: "Daysi Pico", text: "Sufría de taquicardia crónica y mis latidos eran muy acelerados, lo que afectaba mi vida diaria. Gracias a los tratamientos del Dr. Baus, en solo tres semanas he mejorado notablemente y me siento mucho más tranquila." },
];

const HERO = "https://images.unsplash.com/photo-1530026186672-2cd00ffc50fe?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600";
const DOCTOR = DOCTOR_DESK;
const HEART_IMG = "https://images.unsplash.com/photo-1460672985063-6764ac8b9c74?crop=entropy&cs=srgb&fm=jpg&q=85&w=900";

const SERVICES = [
  { icon: HeartPulse, title: "CARDIOLOGÍA", desc: "Prevención, diagnóstico y tratamiento de las enfermedades del corazón y del sistema cardiovascular." },
  { icon: Wind, title: "CARDIOPULMONAR", desc: "Insuficiencia cardíaca, hipertensión pulmonar y afecciones respiratorias asociadas." },
  { icon: Droplets, title: "CARDIORENAL", desc: "Relación corazón–riñón: prevención y tratamiento temprano preservando la función renal." },
  { icon: Activity, title: "CARDIOMETABÓLICO", desc: "Diabetes, obesidad e hipertensión: reducimos el riesgo cardiovascular con hábitos saludables." },
];


function Nav() {
  const [open, setOpen] = useState(false);
  const links = [
    ["#servicios", "Servicios", "nav-servicios"],
    ["#sobre-mi", "Sobre mí", "nav-sobre-mi"],
    ["#testimonios", "Testimonios", "nav-testimonios"],
    ["#contacto", "Contacto", "nav-contacto"],
  ];
  return (
    <nav className="fixed top-0 inset-x-0 z-40 backdrop-blur-md bg-[#0A0F1D]/90 border-b border-white/5">
      <div className="max-w-7xl mx-auto px-5 sm:px-8 h-20 flex items-center justify-between">
        <Logo className="h-9" />
        <div className="hidden md:flex items-center gap-8 text-sm text-slate-300 font-medium">
          {links.map(([h, t, id]) => (
            <a key={id} href={h} className="hover:text-[#38BDF8] transition" data-testid={id}>{t}</a>
          ))}
        </div>
        <div className="hidden md:flex items-center gap-3">
          <Link to="/login" data-testid="nav-login" className="text-sm text-slate-300 hover:text-white transition">
            Acceso clínico
          </Link>
          <a href={WHATSAPP_LINK} target="_blank" rel="noopener noreferrer" data-testid="nav-cita"
             className="bg-gradient-to-r from-[#EC4899] to-[#E11D48] text-white text-sm font-semibold px-4 py-2 rounded-full hover:opacity-90 transition">
            Agendar cita
          </a>
        </div>
        <button onClick={() => setOpen((o) => !o)} data-testid="mobile-menu-toggle"
          className="md:hidden w-10 h-10 flex items-center justify-center rounded-lg border border-white/15 text-white">
          {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
        </button>
      </div>

      {open && (
        <div className="md:hidden bg-[#0A0F1D] border-t border-white/10 px-5 py-5 space-y-2 cb-fade-up" data-testid="mobile-menu">
          {links.map(([h, t, id]) => (
            <a key={id} href={h} onClick={() => setOpen(false)} data-testid={`m-${id}`}
              className="block py-2.5 text-slate-200 hover:text-[#38BDF8] border-b border-white/5 transition">{t}</a>
          ))}
          <Link to="/login" onClick={() => setOpen(false)} data-testid="mobile-nav-login"
            className="block mt-3 text-center border border-white/20 text-white font-semibold py-3 rounded-xl hover:bg-white/5 transition">
            Acceso clínico
          </Link>
          <a href={WHATSAPP_LINK} target="_blank" rel="noopener noreferrer" onClick={() => setOpen(false)} data-testid="mobile-nav-cita"
            className="block text-center bg-gradient-to-r from-[#EC4899] to-[#E11D48] text-white font-semibold py-3 rounded-xl hover:opacity-90 transition">
            Agendar cita
          </a>
        </div>
      )}
    </nav>
  );
}

export default function Landing() {
  const [form, setForm] = useState({ nombre: "", telefono: "", motivo: "", mensaje: "" });
  const [sending, setSending] = useState(false);

  const submitContact = async (e) => {
    e.preventDefault();
    if (!form.nombre || !form.telefono) { toast.error("Completa tu nombre y teléfono"); return; }
    setSending(true);
    try {
      await api.post("/contact", form);
      toast.success("¡Gracias! Te contactaremos pronto.");
      setForm({ nombre: "", telefono: "", motivo: "", mensaje: "" });
    } catch (e) {
      toast.error("No se pudo enviar. Intenta por WhatsApp.");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="bg-[#0A0F1D] text-white overflow-x-hidden">
      <Nav />
      <WhatsAppFab />

      {/* HERO */}
      <section className="relative min-h-screen flex items-center pt-20">
        <div className="absolute inset-0">
          <img src={HERO} alt="Cardiología" className="w-full h-full object-cover opacity-25" />
          <div className="absolute inset-0 bg-gradient-to-br from-[#0A0F1D] via-[#0A0F1D]/85 to-[#02040A]" />
        </div>
        <div className="absolute -top-10 right-0 w-[500px] h-[500px] rounded-full bg-[#EC4899]/10 blur-3xl" />
        <div className="relative max-w-7xl mx-auto px-5 sm:px-8 grid lg:grid-cols-2 gap-10 items-center">
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }}>
            <span className="font-mono-cb text-xs uppercase tracking-[0.25em] text-[#38BDF8]">{CLINIC.doctor} · {CLINIC.tagline}</span>
            <h1 className="mt-4 text-4xl sm:text-5xl lg:text-6xl font-head font-extrabold tracking-tight leading-[1.05]">
              TU CORAZÓN ES <span className="bg-gradient-to-r from-[#EC4899] to-[#38BDF8] bg-clip-text text-transparent">MI PRIORIDAD</span>
            </h1>
            <p className="mt-6 text-slate-300 text-lg max-w-xl leading-relaxed">
              Atención cardiológica integral, personalizada y basada en evidencia médica. Con más de 8 años de experiencia trabajando por la salud de tu corazón.
            </p>
            <div className="mt-8 flex flex-wrap gap-4">
              <a href={WHATSAPP_LINK} target="_blank" rel="noopener noreferrer" data-testid="hero-whatsapp"
                 className="bg-[#25D366] text-white font-semibold px-6 py-3.5 rounded-full hover:scale-105 transition-transform flex items-center gap-2">
                <Phone className="w-4 h-4" /> Escríbeme por WhatsApp
              </a>
              <a href="#servicios" data-testid="hero-servicios"
                 className="border border-white/15 text-white font-semibold px-6 py-3.5 rounded-full hover:bg-white/5 transition flex items-center gap-2">
                Conocer servicios <ChevronRight className="w-4 h-4" />
              </a>
            </div>
            <div className="mt-10 flex gap-8">
              {[["8+", "Años de experiencia"], ["4", "Subespecialidades"]].map(([n, l]) => (
                <div key={l}>
                  <div className="text-3xl font-head font-bold text-white">{n}</div>
                  <div className="text-xs text-slate-400 mt-1">{l}</div>
                </div>
              ))}
            </div>
          </motion.div>
          <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.9, delay: 0.2 }} className="hidden lg:block">
            <div className="relative flex items-end justify-center">
              <div className="absolute inset-x-0 bottom-0 h-[85%] bg-gradient-to-tr from-[#EC4899]/25 to-[#38BDF8]/25 blur-3xl rounded-full" />
              <img src={DOCTOR} alt={CLINIC.doctor} className="relative w-full max-h-[600px] object-contain drop-shadow-2xl" />
            </div>
          </motion.div>
        </div>
      </section>

      {/* SERVICIOS con corazón al centro */}
      <section id="servicios" className="py-28 relative bg-gradient-to-b from-[#EEF3FB] via-white to-[#E8F1FA]">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <div className="text-center mb-16">
            <span className="font-mono-cb text-xs uppercase tracking-[0.25em] text-[#E11D48]">Nuestras especialidades</span>
            <h2 className="mt-3 text-3xl sm:text-4xl font-head font-bold text-slate-900">Cardiología que cuida cada sistema</h2>
            <p className="mt-3 text-slate-500 max-w-xl mx-auto">Un enfoque integral para proteger tu corazón desde cada ángulo.</p>
          </div>

          <div className="grid lg:grid-cols-[1fr_auto_1fr] gap-8 items-center">
            <div className="space-y-8">
              {SERVICES.slice(0, 2).map((s, i) => (
                <ServiceCard key={s.title} s={s} align="right" delay={i * 0.1} />
              ))}
            </div>

            <div className="flex justify-center order-first lg:order-none">
              <div className="relative">
                <div className="absolute inset-0 rounded-full bg-gradient-to-tr from-[#EC4899]/40 to-[#38BDF8]/40 blur-3xl cb-heartbeat" />
                <div className="relative w-56 h-56 sm:w-72 sm:h-72 rounded-full overflow-hidden border-4 border-white shadow-2xl">
                  <img src={HEART_IMG} alt="Corazón" className="w-full h-full object-cover" />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#E11D48]/30 to-transparent" />
                  <Heart className="absolute inset-0 m-auto w-16 h-16 text-white drop-shadow cb-heartbeat" fill="#EC4899" />
                </div>
              </div>
            </div>

            <div className="space-y-8">
              {SERVICES.slice(2, 4).map((s, i) => (
                <ServiceCard key={s.title} s={s} align="left" delay={i * 0.1} />
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* SOBRE MI */}
      <section id="sobre-mi" className="py-24 bg-gradient-to-b from-transparent to-[#070A14]">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 grid lg:grid-cols-2 gap-14 items-center">
          <div className="relative">
            <div className="absolute -inset-4 bg-gradient-to-tr from-[#38BDF8]/20 to-[#EC4899]/20 blur-2xl rounded-3xl" />
            <div className="relative rounded-3xl w-full h-[460px] border border-white/10 overflow-hidden">
              <img src={DOCTOR_HEART} alt={CLINIC.doctor} className="w-full h-full object-cover" />
            </div>
          </div>
          <div>
            <span className="font-mono-cb text-xs uppercase tracking-[0.25em] text-[#38BDF8]">Sobre mí</span>
            <h2 className="mt-3 text-3xl sm:text-4xl font-head font-bold">{CLINIC.doctor}</h2>
            <p className="mt-5 text-slate-300 leading-relaxed">
              Con más de 8 años de experiencia, he dedicado mi carrera a la prevención, diagnóstico y tratamiento de enfermedades cardiovasculares, brindando una atención humana, clara y enfocada en el bienestar a largo plazo de mis pacientes.
            </p>
            <div className="mt-6 grid sm:grid-cols-2 gap-4">
              {[
                { icon: Award, t: "Formación internacional", d: "Harvard Medical School · SIAC Colombia · Río de Janeiro · Guayaquil" },
                { icon: ShieldCheck, t: "Ambiente seguro", d: "Espacio confortable con los más altos estándares de bioseguridad" },
                { icon: Cpu, t: "Tecnología de punta", d: "Monitoreo cardíaco no invasivo de última generación" },
                { icon: Stethoscope, t: "Atención personalizada", d: "Cada paciente, un plan de cuidado único y basado en evidencia" },
              ].map((f) => (
                <div key={f.t} className="bg-[#0F172A]/70 border border-white/10 rounded-2xl p-4">
                  <f.icon className="w-6 h-6 text-[#EC4899] mb-2" />
                  <div className="font-semibold text-sm">{f.t}</div>
                  <div className="text-xs text-slate-400 mt-1">{f.d}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>


      {/* TESTIMONIOS */}
      <section id="testimonios" className="py-24 bg-[#070A14]">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <div className="max-w-2xl mb-12">
            <span className="font-mono-cb text-xs uppercase tracking-[0.25em] text-[#EC4899]">Testimonios</span>
            <h2 className="mt-3 text-3xl sm:text-4xl font-head font-bold">El mejor testimonio es un corazón sano</h2>
          </div>
          <div className="grid md:grid-cols-3 gap-6" data-testid="testimonials">
            {TESTIMONIALS.map((t, i) => (
              <motion.figure key={t.name} initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
                transition={{ duration: 0.6, delay: i * 0.1 }}
                className="h-full flex flex-col bg-[#0F172A]/70 border border-white/10 rounded-2xl p-7 hover:border-[#EC4899]/40 transition">
                <Quote className="w-7 h-7 text-[#EC4899] mb-4" />
                <blockquote className="text-slate-300 leading-relaxed flex-1">“{t.text}”</blockquote>
                <figcaption className="flex items-center gap-3 pt-5 mt-6 border-t border-white/10">
                  <span className="w-10 h-10 rounded-full bg-gradient-to-tr from-[#EC4899] to-[#38BDF8] text-white flex items-center justify-center font-head font-bold text-sm">
                    {t.name.split(" ").map((w) => w[0]).slice(0, 2).join("")}
                  </span>
                  <span className="font-head font-semibold text-white">{t.name}</span>
                </figcaption>
              </motion.figure>
            ))}
          </div>
        </div>
      </section>

      {/* CONTACTO / FOOTER */}
      <footer id="contacto" className="bg-[#070A14] border-t border-white/5 pt-20 pb-10">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 grid lg:grid-cols-2 gap-14">
          <div>
            <Logo className="h-10" />
            <p className="mt-5 text-slate-400 max-w-md leading-relaxed">
              Atención cardiológica integral con un enfoque humano y basado en evidencia. Agenda tu consulta y cuida tu corazón hoy.
            </p>
            <div className="mt-7 space-y-4 text-sm">
              <div className="flex items-start gap-3"><MapPin className="w-5 h-5 text-[#EC4899] mt-0.5" /><span className="text-slate-300">{CLINIC.address}</span></div>
              <div className="flex items-start gap-3"><Phone className="w-5 h-5 text-[#EC4899] mt-0.5" /><span className="text-slate-300">{CLINIC.phone}</span></div>
              <div className="flex items-start gap-3"><Mail className="w-5 h-5 text-[#EC4899] mt-0.5" /><span className="text-slate-300">{CLINIC.email}</span></div>
              <div className="flex items-start gap-3">
                <Clock className="w-5 h-5 text-[#EC4899] mt-0.5" />
                <div className="text-slate-300">
                  {CLINIC.hours.map((h) => (
                    <div key={h.d} className="flex gap-2"><span className="w-36 text-slate-400">{h.d}</span><span>{h.h}</span></div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          <div className="bg-[#0F172A]/70 border border-white/10 rounded-3xl p-7">
            <h3 className="font-head text-xl font-bold">Agenda tu consulta</h3>
            <p className="text-slate-400 text-sm mt-1 mb-5">Déjame tus datos y te contactaré a la brevedad.</p>
            <form onSubmit={submitContact} className="space-y-3">
              <input data-testid="contact-nombre" value={form.nombre} onChange={(e) => setForm({ ...form, nombre: e.target.value })}
                placeholder="Tu nombre" className="w-full bg-[#0A0F1D] border border-white/10 rounded-xl px-4 py-3 text-sm focus:border-[#38BDF8] outline-none" />
              <input data-testid="contact-telefono" value={form.telefono} onChange={(e) => setForm({ ...form, telefono: e.target.value })}
                placeholder="Tu teléfono celular" className="w-full bg-[#0A0F1D] border border-white/10 rounded-xl px-4 py-3 text-sm focus:border-[#38BDF8] outline-none" />
              <input data-testid="contact-motivo" value={form.motivo} onChange={(e) => setForm({ ...form, motivo: e.target.value })}
                placeholder="Motivo de consulta" className="w-full bg-[#0A0F1D] border border-white/10 rounded-xl px-4 py-3 text-sm focus:border-[#38BDF8] outline-none" />
              <textarea data-testid="contact-mensaje" value={form.mensaje} onChange={(e) => setForm({ ...form, mensaje: e.target.value })}
                placeholder="Cuéntame cómo te sientes (opcional)" rows={3}
                className="w-full bg-[#0A0F1D] border border-white/10 rounded-xl px-4 py-3 text-sm focus:border-[#38BDF8] outline-none resize-none" />
              <button type="submit" disabled={sending} data-testid="contact-submit"
                className="w-full bg-gradient-to-r from-[#EC4899] to-[#E11D48] text-white font-semibold py-3 rounded-xl hover:opacity-90 transition disabled:opacity-50 flex items-center justify-center gap-2">
                <Send className="w-4 h-4" /> {sending ? "Enviando…" : "Enviar"}
              </button>
            </form>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-5 sm:px-8 mt-14 pt-8 border-t border-white/5 flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="text-slate-500 text-xs">© {new Date().getFullYear()} {CLINIC.name} · {CLINIC.doctor}. Todos los derechos reservados.</p>
          <Link to="/login" className="text-slate-500 hover:text-white text-xs transition" data-testid="footer-login">Acceso clínico</Link>
        </div>
      </footer>
    </div>
  );
}

function ServiceCard({ s, align, delay }) {
  return (
    <motion.div
      initial={{ opacity: 0, x: align === "right" ? -30 : 30 }}
      whileInView={{ opacity: 1, x: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.5, delay }}
      data-testid={`service-${s.title.toLowerCase()}`}
      className={`bg-white border border-slate-200 shadow-sm rounded-2xl p-6 hover:shadow-xl hover:border-[#38BDF8]/40 hover:-translate-y-1 transition ${align === "right" ? "lg:text-right" : "lg:text-left"}`}
    >
      <div className={`flex items-center gap-3 mb-3 ${align === "right" ? "lg:flex-row-reverse" : ""}`}>
        <div className="w-11 h-11 rounded-xl bg-gradient-to-tr from-[#EC4899]/15 to-[#38BDF8]/15 flex items-center justify-center shrink-0">
          <s.icon className="w-5 h-5 text-[#E11D48]" />
        </div>
        <h3 className="font-head font-bold tracking-wide text-slate-900">{s.title}</h3>
      </div>
      <p className="text-sm text-slate-500 leading-relaxed">{s.desc}</p>
    </motion.div>
  );
}
